import React from 'react';
import moment from 'moment';
import { FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { DataTable, Pill } from '../../../components/EntityList';

function renderAmount(amount, currency, formatCurrency) {
	return amount > 0 ? formatCurrency(amount, currency) : <span className='tw:text-ink-4'>—</span>;
}

/**
 * "Ingresos + Pagos" two-pane panel from the Inicio handoff spec — replaces
 * FacturacionModule's net-income callout + payments-by-category table with
 * the same underlying data, just restyled to the entity-list token system.
 */
function IncomePaymentsPanel({
	loading,
	loaded,
	paymentsUSD,
	paymentsUYU,
	previousMonthPaymentsUSD,
	previousMonthPaymentsUYU,
	incomeChangePercentUSD,
	incomeChangePercentUYU,
	allConcepts,
	paymentsByConcept,
	duesByConcept,
	totalPaid,
	totalPending,
	totalOverdue,
	formatCurrency,
}) {
	const rows = allConcepts.map((concept) => {
		const paymentData = paymentsByConcept[concept] || { name: concept, paid: { amountUSD: 0, amountUYU: 0 } };
		const dueData = duesByConcept[concept] || {
			pending: { amountUSD: 0, amountUYU: 0 },
			overdue: { amountUSD: 0, amountUYU: 0 },
		};
		return {
			key: concept,
			name: paymentData.name || concept,
			paidUSD: paymentData.paid.amountUSD,
			paidUYU: paymentData.paid.amountUYU,
			pendingUSD: dueData.pending.amountUSD,
			pendingUYU: dueData.pending.amountUYU,
			overdueUSD: dueData.overdue.amountUSD,
			overdueUYU: dueData.overdue.amountUYU,
			formatCurrency,
		};
	});

	const footerRow = {
		name: 'Total',
		paidUSD: totalPaid.amountUSD,
		paidUYU: totalPaid.amountUYU,
		pendingUSD: totalPending.amountUSD,
		pendingUYU: totalPending.amountUYU,
		overdueUSD: totalOverdue.amountUSD,
		overdueUYU: totalOverdue.amountUYU,
		formatCurrency,
	};

	const columns = [
		{ key: 'name', header: 'Concepto', render: (row) => <span className='tw:font-medium'>{row.name}</span> },
		{ key: 'paidUSD', header: 'Pagos USD', className: 'tw:text-right tw:font-mono', render: (row) => renderAmount(row.paidUSD, 'USD', row.formatCurrency) },
		{ key: 'paidUYU', header: 'Pagos UYU', className: 'tw:text-right tw:font-mono', render: (row) => renderAmount(row.paidUYU, 'UYU', row.formatCurrency) },
		{ key: 'pendingUSD', header: 'Pend. USD', className: 'tw:text-right tw:font-mono', render: (row) => renderAmount(row.pendingUSD, 'USD', row.formatCurrency) },
		{ key: 'pendingUYU', header: 'Pend. UYU', className: 'tw:text-right tw:font-mono', render: (row) => renderAmount(row.pendingUYU, 'UYU', row.formatCurrency) },
		{
			key: 'overdueUSD',
			header: 'Venc. USD',
			className: 'tw:text-right tw:font-mono',
			render: (row) => (row.overdueUSD > 0 ? <span className='tw:text-danger'>{formatCurrency(row.overdueUSD, 'USD')}</span> : <span className='tw:text-ink-4'>—</span>),
		},
		{
			key: 'overdueUYU',
			header: 'Venc. UYU',
			className: 'tw:text-right tw:font-mono',
			render: (row) => (row.overdueUYU > 0 ? <span className='tw:text-danger'>{formatCurrency(row.overdueUYU, 'UYU')}</span> : <span className='tw:text-ink-4'>—</span>),
		},
	];

	const deltaPill = (percent, previousAmount) => {
		if (previousAmount <= 0) return null;
		const value = parseFloat(percent);
		return (
			<Pill tone={value >= 0 ? 'ok' : 'danger'}>
				{value >= 0 ? <FaArrowUp size={9} /> : <FaArrowDown size={9} />}
				{value >= 0 ? '+' : ''}{percent}% vs {moment().subtract(1, 'month').format('MMMM')}
			</Pill>
		);
	};

	return (
		<div className='tw:grid tw:grid-cols-[minmax(220px,1fr)_2.4fr] tw:max-lg:grid-cols-1 tw:gap-4 tw:mb-6 tw:items-stretch'>
			<div className='tw:p-5 tw:bg-surface tw:border tw:border-border tw:rounded-panel tw:flex tw:flex-col tw:gap-3'>
				<span className='tw:text-[13.5px] tw:font-semibold tw:text-ink-0'>Ingresos {moment().format('MMMM')}</span>
				<div className='tw:flex tw:flex-col tw:gap-2'>
					<div className='tw:rounded-token tw:bg-accent tw:text-white tw:px-4 tw:py-3'>
						<div className='tw:text-[12px] tw:font-semibold tw:opacity-80'>UYU</div>
						<div className='tw:font-mono tw:text-[20px] tw:font-semibold tw:tabular-nums'>{loaded ? formatCurrency(paymentsUYU, 'UYU') : '—'}</div>
						{loaded && deltaPill(incomeChangePercentUYU, previousMonthPaymentsUYU)}
					</div>
					<div className='tw:rounded-token tw:bg-accent-hover tw:text-white tw:px-4 tw:py-3'>
						<div className='tw:text-[12px] tw:font-semibold tw:opacity-80'>USD</div>
						<div className='tw:font-mono tw:text-[20px] tw:font-semibold tw:tabular-nums'>{loaded ? formatCurrency(paymentsUSD, 'USD') : '—'}</div>
						{loaded && deltaPill(incomeChangePercentUSD, previousMonthPaymentsUSD)}
					</div>
				</div>
			</div>
			<DataTable
				columns={columns}
				rows={rows}
				rowKey={(row) => row.key}
				footerRow={footerRow}
				emptyMessage='No hay datos disponibles.'
				// allConcepts comes from a separate DebtConceptService fetch, not the
				// reducer's `loading` — gate on both so the table doesn't flip to its
				// empty message before concepts arrive.
				loading={loading || allConcepts.length === 0}
				skeleton
				skeletonRows={4}
			/>
		</div>
	);
}

export default IncomePaymentsPanel;
